const TYPE_MAP = {
  flight: 'air',
};

/**
 * Renames a v1 travel type to its console-v2 equivalent, passing unknown types through unchanged.
 * @param {String} type - v1 travel type (e.g. 'flight', 'hotel')
 * @returns {String}
 */
export function mapTravelType(type) {
  return TYPE_MAP[type] || type;
}

/**
 * Builds a URL to the console-v2 travel page.
 * @param {Object} options
 * @param {String} options.teamId - current team id, e.g. from store.getters['me/teamId']
 * @param {Array.<{condition: String, operator: String, values: Array.<String>}>} [options.filters] - FilterBuilder filters; omit to let console-v2 apply its own default
 * @param {String} [options.type] - flat top-level type param, translated via mapTravelType
 * @param {String} [options.sort] - flat top-level sort param, e.g. 'created:desc'
 * @returns {String}
 */
export function buildV2TravelUrl({ teamId, filters = [], type, sort }) {
  let params = serializeFilters(filters);

  if (type) {
    params.push(`type=${encodeURIComponent(mapTravelType(type))}`);
  }

  if (sort) {
    params.push(`sort=${encodeURIComponent(sort)}`);
  }

  let query = params.length ? `?${params.join('&')}` : '';

  return `/v2/orgs/${teamId}/travel${query}`;
}

/**
 * Builds a URL to a console-v2 trip page.
 * @param {Object} options
 * @param {String|Number} options.teamId - current team id, e.g. from store.getters['me/teamId']
 * @param {String|Number} options.tripId
 * @returns {String}
 */
export function buildV2TripUrl({ teamId, tripId }) {
  return `/v2/orgs/${teamId}/trips/${tripId}`;
}

/**
 * Builds a URL to the console-v2 organization root. console-v2 sends the user on to the dashboard or Travel.
 * @param {Object} options
 * @param {String|Number} options.teamId - current team id, e.g. from store.getters['me/teamId']
 * @returns {String}
 */
export function buildV2OrgUrl({ teamId }) {
  return `/v2/orgs/${teamId}`;
}

/**
 * Builds a URL to the console-v2 organization dashboard.
 * @param {Object} options
 * @param {String|Number} options.teamId - current team id, e.g. from store.getters['me/teamId']
 * @param {String} [options.period] - dashboard period, e.g. 'year_prev'; omit to let console-v2 apply its default
 * @returns {String}
 */
export function buildV2DashboardUrl({ teamId, period }) {
  let query = period ? `?period=${encodeURIComponent(period)}` : '';

  return `/v2/orgs/${teamId}/dashboard${query}`;
}

/**
 * Serializes a filters array into console-v2 FilterBuilder bracket-notation query params.
 * @param {Array.<{condition: String, operator: String, values: Array.<String>}>} filters
 * @returns {Array.<String>}
 */
function serializeFilters(filters) {
  let params = [];

  filters.forEach((filter, index) => {
    params.push(`filters[${index}][condition]=${encodeURIComponent(filter.condition)}`);
    params.push(`filters[${index}][operator]=${encodeURIComponent(filter.operator)}`);
    filter.values.forEach((value, valueIndex) => {
      params.push(`filters[${index}][values][${valueIndex}]=${encodeURIComponent(value)}`);
    });
  });

  return params;
}
